import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@nextui-org/react';
import { useGetAiWorkerUsageByIdMutation, useGetTagsDataMutation, useGetTenantMetadataQuery, useTypedSelector, RootState } from '@circuitry-ai/doc-data';
import {
    useUxgenieTemplateIdForAgent,
    CtyShowToast,
    checkType,
    injectPalChatbot,
    UxgenieTemplateContentView,
    type UxgenieTemplateEvent,
    extractRendererData,
    resolveActionOutputForAgent,
    extractReceivedClaimDataFromClaimScoreData,
    extractClaimIntakeData,
    getActionOutputV2Payload,
    getV2SourcesTemplateData,
    useUxgenieTemplateHtmlResolver,
    getPalSessionMode,
    useSyncDefaultAdvisorPalMode,
} from '@circuitry-ai/doc-ui';
import { AdjustmentDetailsModal } from './adjustment/AdjustmentDetailsModal';
import {
    findAgentRunByName,
    isAdjustmentLinkClicked,
    type AdjustmentLinkEvent,
    type TagAgentRun,
} from './adjustment/adjustmentDetailsExtractors';

/** Agents whose PAL dock is mounted closed (icon only). Add names here to opt in. */
const AGENTS_WITH_CLOSED_PAL_DOCK = ['Simulation Adjudicator Agent'];

const normalizeAgentLabel = (name?: string): string =>
    (name ?? '').replace(/_/g, ' ').trim().toLowerCase();

const shouldStartWithClosedPalDock = (agentName?: string): boolean => {
    const wanted = normalizeAgentLabel(agentName);
    return Boolean(wanted) && AGENTS_WITH_CLOSED_PAL_DOCK.some((name) => normalizeAgentLabel(name) === wanted);
};

/**
 * AgentUxgenieTemplateViewPage - header-less, full-page UXGenie template view routed
 * by an agent's aiworker_id, the row's entity id, and the row's own aiworker_usage_id
 * (e.g. /agents/history/:aiworker_id/:entity_id/:aiworker_usage_id), reached via the
 * "View Details" icon in the Agent History table's Entity ID column (opened in a new tab).
 *
 * Resolves the agent's configured uxgenie_template attribute to a template usage id to
 * get the JSX template code (shared across every row of this agent), then fetches the
 * row's own usage record (aiworker_usage_id) to get the claim/entity data specific to
 * that row — mirroring the same extraction used by the Sources-tab preview in
 * ClaimScoreGeneratorRenderer — and binds that dynamic data into the template. Renders
 * via the shared UxgenieTemplateContentView — no breadcrumb/header/footer chrome.
 */
export const AgentUxgenieTemplateViewPage: React.FC = () => {
    const { t } = useTranslation();
    const { aiworker_id, entity_id, aiworker_usage_id } = useParams<{ aiworker_id: string; entity_id: string; aiworker_usage_id: string }>();

    // A fresh tab always boots with an empty auth token in the store (it's populated
    // asynchronously by the app shell). Wait for it before firing any request —
    // otherwise the first request goes out unauthenticated and fails silently.
    const token = useTypedSelector((state: RootState) => state.auth.token);
    const isReadyToFetch = Boolean(token && aiworker_id);

    const { templateId, isFetching: isResolvingTemplateId, error: resolveError } = useUxgenieTemplateIdForAgent(
        aiworker_id,
        isReadyToFetch
    );

    const [getTemplateById, { isLoading: isLoadingTemplateData }] = useGetAiWorkerUsageByIdMutation();
    const [templateData, setTemplateData] = useState<any>(null);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const { resolveHtml, resolveHtmlUrl } = useUxgenieTemplateHtmlResolver();
    const [resolvedHtml, setResolvedHtml] = useState<string | null>(null);
    const [resolvedHtmlUrl, setResolvedHtmlUrl] = useState<string | null>(null);
    const [isResolvingHtml, setIsResolvingHtml] = useState(false);
    const [adjustmentEvent, setAdjustmentEvent] = useState<AdjustmentLinkEvent | null>(null);
    const [getTagsData] = useGetTagsDataMutation();
    // Agent runs already fetched for this page, keyed by event.agentName — a repeat
    // click for the same agent reuses the stored run instead of refetching.
    const agentRunsByNameRef = useRef<Map<string, TagAgentRun>>(new Map());
    const [adjustmentAgentRun, setAdjustmentAgentRun] = useState<TagAgentRun | null>(null);
    const [isLoadingAgentRun, setIsLoadingAgentRun] = useState(false);
    const [agentRunError, setAgentRunError] = useState<string | null>(null);
    const [getClaimUsageById, { isLoading: isLoadingClaimData }] = useGetAiWorkerUsageByIdMutation();
    const [claimUsageData, setClaimUsageData] = useState<any>(null);
    const [claimFetchError, setClaimFetchError] = useState<string | null>(null);

    // This page is opened standalone (new tab, no app header), so the PAL chatbot
    // widget — normally launched from the header's "Open PAL" button — is injected
    // here directly so it's still reachable from this view.
    const { data: tenantMetadata } = useGetTenantMetadataQuery(undefined, { skip: !token });
    useSyncDefaultAdvisorPalMode();
    const { data: claimRendererData, agentName: claimAgentName } = useMemo(
        () => extractRendererData(claimUsageData),
        [claimUsageData]
    );
    const closePalDockByDefault = shouldStartWithClosedPalDock(claimAgentName);

    useEffect(() => {
        if (!token || !tenantMetadata) return;
        if (aiworker_usage_id && !claimUsageData) return;

        injectPalChatbot(CtyShowToast, checkType, tenantMetadata?.widget_icon_url, {
            open: !closePalDockByDefault,
            resolveTargetMode: closePalDockByDefault ? () => 'dock' : () => getPalSessionMode(),
        }, 'dock');
    }, [token, tenantMetadata?.widget_icon_url, aiworker_usage_id, claimUsageData, closePalDockByDefault]);

    useEffect(() => {
        document.title = entity_id ? `${entity_id} - Template` : 'UXGenie Template';
    }, [entity_id]);

    useEffect(() => {
        if (!templateId) return;

        let cancelled = false;
        setTemplateData(null);
        setFetchError(null);

        getTemplateById(templateId)
            .unwrap()
            .then((response: any) => {
                if (cancelled) return;
                let data = response;
                if (Array.isArray(response) && response.length > 0) {
                    data = response[0];
                } else if (response?.data) {
                    data = Array.isArray(response.data) ? response.data[0] : response.data;
                }
                setTemplateData(data);
            })
            .catch(() => {
                if (cancelled) return;
                setFetchError('Failed to load template');
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId]);

    // If the template's HTML was saved to S3 (WAF blocks large inline HTML), resolve
    // a presigned URL for it once the usage record has loaded — loaded directly via
    // iframe `src` rather than fetching the text and inlining it as `srcDoc`.
    useEffect(() => {
        const outputData = templateData?.action_output || templateData?.output_data;
        const inlineCode = templateData?.action_output?.generated_code || templateData?.output_data?.generated_code;
        if (inlineCode || outputData?.output_type !== 'html' || outputData?.storage !== 's3') {
            setResolvedHtml(null);
            setResolvedHtmlUrl(null);
            return;
        }

        const aiworker_id = templateData?.aiworker_id;
        const aiworker_usage_id = templateData?.aiworker_usage_id || templateId;
        const sourceId = outputData.source_id;
        if (!aiworker_id || !aiworker_usage_id) return;

        let cancelled = false;
        setIsResolvingHtml(true);
        setResolvedHtml(null);
        setResolvedHtmlUrl(null);

        const resolve = sourceId
            ? resolveHtmlUrl({
                aiworker_id,
                aiworker_usage_id,
                source_id: sourceId,
                extension: outputData.html_file_name?.split('.').pop() || 'html',
            }).then((url) => { if (!cancelled) setResolvedHtmlUrl(url); })
            // Fallback for templates saved before `source_id` was stored on `action_output`
            : resolveHtml({ aiworker_id, aiworker_usage_id, fileName: outputData.html_file_name })
                .then((html) => { if (!cancelled) setResolvedHtml(html); });

        resolve
            .catch((error) => { console.error('Failed to resolve saved HTML template from storage:', error); })
            .finally(() => { if (!cancelled) setIsResolvingHtml(false); });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateData, templateId, resolveHtml, resolveHtmlUrl]);

    // Fetch the row's own usage record — this holds the actual claim/entity data for
    // this specific row, distinct from the template's own (static/sample) usage record.
    useEffect(() => {
        if (!isReadyToFetch || !aiworker_usage_id) return;

        let cancelled = false;
        setClaimUsageData(null);
        setClaimFetchError(null);

        getClaimUsageById(aiworker_usage_id)
            .unwrap()
            .then((response: any) => {
                if (cancelled) return;
                let data = response;
                if (Array.isArray(response) && response.length > 0) {
                    data = response[0];
                } else if (response?.data) {
                    data = Array.isArray(response.data) ? response.data[0] : response.data;
                }
                setClaimUsageData(data);
            })
            .catch(() => {
                if (cancelled) return;
                setClaimFetchError('Failed to load claim data');
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReadyToFetch, aiworker_usage_id]);

    // Fetch the agent run for an adjustment click only when it isn't already stored
    // for that agentName.
    useEffect(() => {
        const claimNumber = adjustmentEvent?.claimNumber;
        const agentName = adjustmentEvent?.agentName;
        if (!claimNumber || (agentName && agentRunsByNameRef.current.has(agentName))) return;

        let cancelled = false;
        setIsLoadingAgentRun(true);
        setAgentRunError(null);

        getTagsData({ key: 'claimNumber', value: claimNumber })
            .unwrap()
            .then((response: unknown) => {
                const run = findAgentRunByName(response, agentName);
                // Store even if the modal was closed mid-request, so reopening reuses it.
                if (run && agentName) agentRunsByNameRef.current.set(agentName, run);
                if (cancelled) return;
                if (!run) {
                    setAgentRunError('No matching agent run found');
                    return;
                }
                setAdjustmentAgentRun(run);
            })
            .catch(() => {
                if (cancelled) return;
                setAgentRunError('Failed to load agent run');
            })
            .finally(() => {
                if (!cancelled) setIsLoadingAgentRun(false);
            });

        return () => {
            cancelled = true;
        };
    }, [adjustmentEvent, getTagsData]);

    const handleTemplateMessage = useCallback((event: UxgenieTemplateEvent) => {
        if (event?.type === 'domReady' || event?.eventType === 'templateData') return;
        if (isAdjustmentLinkClicked(event)) {
            const { agentName, claimNumber } = event as AdjustmentLinkEvent;
            const storedRun = agentName ? agentRunsByNameRef.current.get(agentName) : undefined;
            setAdjustmentAgentRun(storedRun ?? null);
            setAgentRunError(null);
            setIsLoadingAgentRun(!storedRun && Boolean(claimNumber));
            setAdjustmentEvent(event);
        }
    }, []);

    // `data`/`options` are memoized so they keep the same reference across unrelated
    // re-renders (e.g. opening the adjustment modal) — UxgenieTemplateContentView re-posts
    // `templateData` to the iframe whenever either reference changes, and the template
    // re-runs its own API calls (e.g. tags) on every post.
    const boundData = useMemo(() => {
        const staticTemplateData = templateData?.input_data?.original_data ||
            templateData?.action_output?.generated_schema ||
            templateData?.output_data?.generated_schema || '';

        // Dynamic data for this specific row/entity — same extraction the Sources-tab
        // preview uses (Claim Score / V2 sources, then claim intake mapped_object).
        // Falls back to the template's own static/sample data when nothing resolves.
        const claimScoreData = resolveActionOutputForAgent(
            claimRendererData?.action_output as Record<string, unknown> | undefined,
            claimAgentName
        );
        const v2Payload = getActionOutputV2Payload(claimRendererData?.action_output);
        const dynamicClaimData = extractReceivedClaimDataFromClaimScoreData(claimScoreData, claimAgentName, claimRendererData?.entity_number)
            ?? getV2SourcesTemplateData(v2Payload?.sources)
            ?? extractClaimIntakeData(claimRendererData?.action_output);

        return (aiworker_usage_id && !claimFetchError && dynamicClaimData) || staticTemplateData;
    }, [templateData, claimRendererData, claimAgentName, aiworker_usage_id, claimFetchError]);

    const options = useMemo(() => ({
        claimNumber: entity_id,
        aiworkerUsageId: aiworker_usage_id,
        aiworker_id: claimUsageData?.aiworker_id,
        aiworker_usage_id: claimUsageData?.aiworker_usage_id,
        message_id: claimUsageData?.message_id,
        metadata: claimUsageData?.metadata,
        feedback_history: claimUsageData?.feedback_history,
        comment_history: claimUsageData?.comment_history,
    }), [entity_id, aiworker_usage_id, claimUsageData]);

    const handleCloseAdjustment = useCallback(() => setAdjustmentEvent(null), []);

    if (!aiworker_id) {
        return null;
    }

    const isLoading = !isReadyToFetch || isResolvingTemplateId
        || (!!templateId && isLoadingTemplateData && !templateData)
        || (!!aiworker_usage_id && isLoadingClaimData && !claimUsageData)
        || isResolvingHtml;

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <Spinner size="lg" className="mb-4" />
                <p className="text-gray-600 text-lg">{t('Loading...')}</p>
            </div>
        );
    }

    if (resolveError || fetchError || !templateId || !templateData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <input type="hidden" data-testid="agent-uxgenie-entity-id" id="entityCode" value={entity_id ?? ''} readOnly />
                <p className="text-gray-600 text-lg">{t('Template not found')}</p>
            </div>
        );
    }

    const generatedCode = templateData?.action_output?.generated_code || templateData?.output_data?.generated_code || resolvedHtml || '';
    const isHtmlTemplate = (templateData?.action_output?.output_type || templateData?.output_data?.output_type) === 'html';

    return (
        <>
            <input type="hidden" data-testid="agent-uxgenie-entity-id" id="entityCode" value={entity_id ?? ''} readOnly />
            <UxgenieTemplateContentView
                code={generatedCode}
                data={boundData}
                options={options}
                isHtml={isHtmlTemplate}
                htmlSrc={resolvedHtmlUrl ?? undefined}
                onMessage={handleTemplateMessage}
            />
            {adjustmentEvent && (
                <AdjustmentDetailsModal
                    event={adjustmentEvent}
                    agentRun={adjustmentAgentRun}
                    isLoading={isLoadingAgentRun}
                    error={agentRunError}
                    onClose={handleCloseAdjustment}
                />
            )}
        </>
    );
};

export default AgentUxgenieTemplateViewPage;
